import { Inject, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { OrderService } from './order.service';
import { OrderStatus } from './order.models';
import { CustomerAuthService } from './customer-auth.service';
import { I18nService } from '../../../Shared/i18n/i18n.service';

/**
 * "Your rider is outside" — the alarm, owned at APP scope.
 *
 * WHY THIS IS A SERVICE AND NOT PART OF THE TRACKING PAGE: it used to live in OrderTrackPageComponent,
 * which meant it only existed while that page was open. But a customer waiting on food does not sit on
 * the tracking screen — they browse other shops, open their cart, or put the phone down. Navigating away
 * destroyed the component and the alarm with it, so the one moment the alert exists for was the one
 * moment it could not fire. The event is app-scoped, so the watcher has to be too.
 *
 * SINGLE OWNER. Both the background poll here and the tracking page's own (faster) poll funnel into
 * {@link notice}, so there is exactly one alarm however many things are watching. Without that, standing
 * on the tracking page would arm two alarms chiming out of phase.
 *
 * It still cannot reach a CLOSED tab — browsers do not let a background page play audio. Push
 * notifications remain the only route to a phone in someone's pocket.
 */
@Injectable({ providedIn: 'root' })
export class ArrivalAlertService {
  private readonly orders = inject(OrderService);
  private readonly auth = inject(CustomerAuthService);
  private readonly i18n = inject(I18nService);
  private readonly isBrowser: boolean;

  /** How often to look for orders that have gone out for delivery. Cheap and not time-critical. */
  private static readonly DISCOVER_MS = 60_000;
  /** How often to re-check an in-flight order's proximity. This one IS time-critical. */
  private static readonly WATCH_MS = 15_000;
  private static readonly ALARM_REPEAT_MS = 6000;
  private static readonly SPEAK_REPEAT_MS = 20_000;

  /** The order whose rider is at the door, or null. Drives the global banner and the in-page panel. */
  readonly alert = signal<OrderStatus | null>(null);
  readonly alarmActive = computed(() => this.alert() !== null);
  readonly busy = signal(false);
  readonly error = signal('');

  /** Whether this device can speak Urdu at all. null until the voice list loads. */
  readonly urduVoiceAvailable = signal<boolean | null>(null);

  /**
   * A pre-recorded Urdu announcement, if one has been deployed. null until probed.
   *
   * This exists because device TTS is the weak link: plenty of Android phones — and this project's own
   * Windows dev machines — ship no Urdu voice at all, and Urdu text read by an English voice is not
   * words. A recording is the only way to guarantee Urdu audio on every device.
   *
   * The trade-off is that a fixed clip cannot say the rider's NAME, the distance or the cash amount.
   * That is accepted: the audio's job is to get someone's attention and to the door, and all three
   * details are already on screen in the banner and the panel, read on the way down.
   */
  readonly urduClipAvailable = signal<boolean | null>(null);

  /**
   * Candidates in preference order. TWO extensions on purpose: a clip exported from a phone is usually
   * MP4/AAC even when named ".mp3", and while Chrome sniffs the container, iOS Safari trusts the served
   * MIME type — so ".m4a" is the correct name for that content and is tried first. Probing both means a
   * later rename cannot break this, and neither can shipping the other format.
   */
  private static readonly URDU_CLIP_URLS = [
    '/audio/rider-outside-ur.m4a',
    '/audio/rider-outside-ur.mp3',
  ];

  private urduClip?: HTMLAudioElement;

  /**
   * True when the UI is Urdu and there is NO Urdu audio available at all — no voice AND no recording.
   * Only then is the "spoken alert is in English" notice honest; with a clip present, Urdu is exactly
   * what plays.
   */
  readonly speechFallsBackToEnglish = computed(
    () => this.i18n.lang() === 'ur'
      && this.urduVoiceAvailable() === false
      && this.urduClipAvailable() !== true);

  /** Orders the customer has silenced. Per order, and never re-armed for that order. */
  private readonly muted = new Set<string>();
  /** Orders being polled individually, so a second discovery pass doesn't stack timers. */
  private readonly watched = new Map<string, ReturnType<typeof setInterval>>();

  private discoverTimer?: ReturnType<typeof setInterval>;
  private alarmTimer?: ReturnType<typeof setInterval>;
  private speakTimer?: ReturnType<typeof setInterval>;
  private audioCtx?: AudioContext;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Called once from the ordering layout, so the watch spans every page. */
  start(): void {
    if (!this.isBrowser || this.discoverTimer) return;
    window.addEventListener('pointerdown', this.unlockAudio, { once: true });
    this.primeVoices();
    this.preloadUrduClip();
    this.discover();
    this.discoverTimer = setInterval(() => this.discover(), ArrivalAlertService.DISCOVER_MS);
  }

  /**
   * Feed in a freshly-polled order. The tracking page calls this on its own 5s poll so that a customer
   * who IS watching the screen is alerted on that cadence rather than waiting for this service's 15s
   * one — same single alarm either way.
   */
  notice(order: OrderStatus | undefined | null): void {
    if (order) this.evaluate(order);
  }

  /** Tell the rider we're on our way. Silences the alarm on the tap, not on the next poll. */
  announce(orderId: string): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.error.set('');
    this.stopAlarm();
    this.orders.headingOut(orderId).subscribe({
      next: () => {
        this.busy.set(false);
        // Mark it handled so neither poll re-arms before the server reflects the announcement.
        this.muted.add(orderId);
        this.alert.set(null);
      },
      error: (e) => {
        this.busy.set(false);
        this.error.set(e?.error?.message || this.i18n.t('headout.failed'));
      },
    });
  }

  /** Silence without acknowledging — someone in a meeting needs the sound off, not a promise. */
  mute(orderId: string): void {
    this.muted.add(orderId);
    this.stopAlarm();
    this.alert.set(null);
  }

  // ── Discovery + per-order watch ──────────────────────────────────────────────

  private discover(): void {
    // Nothing to watch for a guest, and the endpoint is authenticated anyway.
    if (!this.auth.isAuthenticated()) { this.stopAll(); return; }

    this.orders.myOrders().subscribe({
      next: (r) => {
        const live = (r.data ?? []).filter((o) => o.status === 'OutForDelivery' && !!o.orderId);
        const liveIds = new Set(live.map((o) => o.orderId));

        // Drop watches for orders that have landed.
        for (const [id, t] of this.watched) {
          if (!liveIds.has(id)) { clearInterval(t); this.watched.delete(id); }
        }
        // Start one for each newly out-for-delivery order.
        for (const o of live) {
          if (this.watched.has(o.orderId)) continue;
          this.pollOne(o.orderId);
          this.watched.set(o.orderId, setInterval(() => this.pollOne(o.orderId), ArrivalAlertService.WATCH_MS));
        }
      },
      error: () => { /* offline — existing watches keep running */ },
    });
  }

  private pollOne(orderId: string): void {
    this.orders.track(orderId).subscribe({
      next: (r) => { if (r.success) this.evaluate(r.data); },
      error: () => {},
    });
  }

  /**
   * The single decision point: should the alarm be ringing for this order? The SERVER owns whether the
   * rider is close enough (canHeadOut) — nothing here re-derives distance.
   */
  private evaluate(order: OrderStatus | undefined): void {
    if (!order?.orderId) return;

    const shouldRing = order.status === 'OutForDelivery'
      && !!order.canHeadOut
      && !order.headingOutAnnouncedAt
      && !this.muted.has(order.orderId);

    const current = this.alert();

    if (!shouldRing) {
      // Only stand down if this is the order we are ringing about — another order may still be due.
      if (current && current.orderId === order.orderId) { this.stopAlarm(); this.alert.set(null); }
      return;
    }

    // Keep the freshest copy so the banner's distance stays current while it rings.
    this.alert.set(order);
    if (this.alarmTimer) return;                     // already ringing — don't restack

    // SPEAK FIRST, then chime. When a recording is in use it is ~6s of actual Urdu speech, which is a
    // stronger attention-getter than a tone anyway — and playChime() suppresses itself while the clip
    // is talking, so leading with speech is what keeps the two from overlapping.
    this.speak();
    this.playChime();
    this.alarmTimer = setInterval(() => this.playChime(), ArrivalAlertService.ALARM_REPEAT_MS);
    this.speakTimer = setInterval(() => this.speak(), ArrivalAlertService.SPEAK_REPEAT_MS);
  }

  private stopAlarm(): void {
    if (this.alarmTimer) clearInterval(this.alarmTimer);
    if (this.speakTimer) clearInterval(this.speakTimer);
    this.alarmTimer = undefined;
    this.speakTimer = undefined;
    try { window.speechSynthesis?.cancel(); } catch { /* unsupported */ }
    // Cut the recording too — muting must silence a clip that is mid-sentence, not just stop the
    // NEXT repeat. Rewound so the following alarm starts from the beginning.
    try {
      if (this.urduClip) { this.urduClip.pause(); this.urduClip.currentTime = 0; }
    } catch { /* element not ready */ }
  }

  private stopAll(): void {
    this.stopAlarm();
    for (const t of this.watched.values()) clearInterval(t);
    this.watched.clear();
    this.alert.set(null);
  }

  // ── Sound + speech ──────────────────────────────────────────────────────────

  private readonly unlockAudio = () => {
    try {
      const AudioCtx = (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx: AudioContext = this.audioCtx ?? (this.audioCtx = new AudioCtx());
      if (ctx.state === 'suspended') void ctx.resume();
    } catch { /* audio unavailable */ }
  };

  /** True while the recorded announcement is mid-sentence. */
  private isUrduClipPlaying(): boolean {
    const el = this.urduClip;
    return !!el && !el.paused && !el.ended && el.currentTime > 0;
  }

  /**
   * Two rising notes, twice — plus a vibration for a phone face-down on a table.
   *
   * Suppressed while the recording is talking. The chime repeats every 6s and a phone-recorded clip is
   * easily longer than that (the deployed Urdu one is 6.2s), so without this guard the tone plays over
   * the voice and makes both harder to understand. The vibration still fires: it carries no words to
   * drown out, and it is the part that reaches a phone in a pocket.
   */
  private playChime(): void {
    if (!this.isBrowser) return;
    try { (navigator as any).vibrate?.([300, 120, 300]); } catch { /* unsupported */ }
    if (this.isUrduClipPlaying()) return;
    try {
      const AudioCtx = (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx: AudioContext = this.audioCtx ?? (this.audioCtx = new AudioCtx());
      if (ctx.state === 'suspended') void ctx.resume();
      const notes: [number, number, number][] = [
        [880, 0.0, 0.16], [1175, 0.18, 0.20],
        [880, 0.42, 0.16], [1175, 0.60, 0.30],
      ];
      for (const [freq, start, dur] of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine'; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start); osc.stop(ctx.currentTime + start + dur);
      }
    } catch { /* audio unavailable — the banner still shows */ }
  }

  /**
   * Probe for a deployed Urdu recording, once, at startup.
   *
   * `loadedmetadata` is the signal used rather than `canplaythrough`: it fires as soon as the browser has
   * understood the container and codec, which is exactly the question being asked — "can this device
   * play this file?" — and it fires reliably, whereas canplaythrough can be deferred indefinitely.
   */
  private preloadUrduClip(): void {
    const tryNext = (i: number): void => {
      if (i >= ArrivalAlertService.URDU_CLIP_URLS.length) {
        this.urduClipAvailable.set(false);   // none deployed — TTS path stands
        return;
      }
      try {
        const el = new Audio();
        el.preload = 'auto';
        el.src = ArrivalAlertService.URDU_CLIP_URLS[i];
        el.addEventListener('loadedmetadata', () => {
          this.urduClip = el;
          this.urduClipAvailable.set(true);
        }, { once: true });
        // 404, wrong codec, or an SPA rewrite handing back index.html — move to the next candidate.
        el.addEventListener('error', () => tryNext(i + 1), { once: true });
        el.load();
      } catch {
        tryNext(i + 1);
      }
    };
    tryNext(0);
  }

  /**
   * Play the recorded Urdu announcement. Returns false if it could not start, so the caller can fall
   * through to TTS rather than the alarm going silent.
   *
   * Rewinds rather than creating a new element each time: one decoded buffer, and rewinding is also what
   * stops a slow repeat overlapping itself.
   */
  private playUrduClip(): boolean {
    const el = this.urduClip;
    if (!el) return false;
    // Still talking from the last repeat — let it finish rather than restarting mid-sentence.
    if (!el.paused && !el.ended && el.currentTime > 0) return true;
    try {
      el.currentTime = 0;
      // Autoplay policy can still reject this; the chime and the banner carry the alert if so.
      void el.play().catch(() => {});
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Say it aloud.
   *
   * ORDER OF PREFERENCE: a real Urdu RECORDING beats synthesis, and synthesis in the UI language beats
   * synthesis in the wrong one. So when the shopper has chosen Urdu we play the deployed clip if there
   * is one; only failing that do we synthesise, and only in Urdu if the device actually has that voice.
   */
  private speak(): void {
    if (!this.isBrowser) return;

    // A recording is authentic Urdu on any device — prefer it over TTS outright.
    if (this.i18n.lang() === 'ur' && this.urduClipAvailable() === true && this.playUrduClip()) return;

    try {
      const synth = window.speechSynthesis;
      if (!synth || synth.speaking || synth.pending) return;

      const order = this.alert();
      if (!order) return;

      const uiLang = this.i18n.lang();
      const voice = uiLang === 'ur' ? this.findVoice('ur') : null;
      const speakLang: 'en' | 'ur' = uiLang === 'ur' && voice ? 'ur' : 'en';

      const who = order.riderName || this.i18n.tIn(speakLang, 'track.yourRider');
      const away = this.spokenDistance(order, speakLang);
      const cash = order.amountToCollect ?? 0;
      const currency = order.currencyCode ?? '';

      let text = away
        ? this.i18n.tIn(speakLang, 'headout.speak', { name: who, away })
        : this.i18n.tIn(speakLang, 'headout.speakNoDistance', { name: who });
      if (cash > 0) text += this.i18n.tIn(speakLang, 'headout.speakCash', { amount: cash, currency });

      const u = new SpeechSynthesisUtterance(text);
      if (voice && speakLang === 'ur') u.voice = voice;
      u.lang = speakLang === 'ur' ? 'ur-PK' : 'en-US';
      u.rate = 0.95;
      u.volume = 1;
      synth.speak(u);
    } catch { /* speech unsupported — the chime and the banner remain */ }
  }

  /** Distance with the unit SPELLED OUT — a TTS voice reads a bare "m" as the letter "em". */
  private spokenDistance(order: OrderStatus, lang: 'en' | 'ur'): string | null {
    const m = order.riderDistanceMeters;
    if (m == null) return null;
    return m < 1000
      ? this.i18n.tIn(lang, 'unit.metresSpoken', { n: Math.max(10, Math.round(m / 10) * 10) })
      : this.i18n.tIn(lang, 'unit.kmSpoken', { n: (m / 1000).toFixed(1) });
  }

  /** Distance as DISPLAYED, for the banner. Rounded — consumer GPS is not metre-accurate. */
  distanceLabel(order: OrderStatus | null): string | null {
    const m = order?.riderDistanceMeters;
    if (m == null) return null;
    return m < 1000
      ? `${Math.max(10, Math.round(m / 10) * 10)} m`
      : `${(m / 1000).toFixed(1)} km`;
  }

  private findVoice(prefix: string): SpeechSynthesisVoice | null {
    try {
      const voices = window.speechSynthesis?.getVoices() ?? [];
      return voices.find((v) => v.lang?.toLowerCase().startsWith(prefix)) ?? null;
    } catch {
      return null;
    }
  }

  /** `getVoices()` is async and empty on the first call — start it early and re-check when it fills. */
  private primeVoices(): void {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      const check = () => {
        const voices = synth.getVoices();
        // Empty means "not loaded", NOT "no Urdu voice" — stay undetermined so no warning flashes up.
        if (voices.length) this.urduVoiceAvailable.set(!!this.findVoice('ur'));
      };
      check();
      synth.addEventListener?.('voiceschanged', check);
    } catch { /* speech unsupported */ }
  }
}
